<template>
	<CRow>
		<CCol>
			<div v-if="showLoginForm" class="divider">or</div>

			<div class="social-group-list">
				<button
					v-for="(item, key) in SOCIAL_PROVIDER_GROUP_LIST"
					:key="key"
					class="btn btn-social"
					:disabled="isProviderLoading(item.provider)"
					@click="onSocialLogin(item.provider)"
				>
					<div class="logo">
						<img :src="item.icon.url" :alt="item.icon.alt" loading="lazy">
					</div>
					<span class="label">
						{{ isProviderLoading(item.provider) ? 'loading...' : item.label }}
					</span>
				</button>
			</div>
		</CCol>
	</CRow>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import { ROUTE_NAME } from '../../enums/route';
import {
	SOCIAL_PROVIDER_GROUP,
	SOCIAL_PROVIDER_GROUP_LIST,
	PROVIDER,
	PROVIDER_KEY,
	REGISTER_STATUS,
	SOCIAL_LOGIN_ERROR_TYPE,
	VERIFIED_STATUS,
} from '../../enums/social';

export default {
	name: 'SocialGroup',
	data() {
		return {
			// Constant
			PROVIDER,
			SOCIAL_PROVIDER_GROUP_LIST,

			// Variable
			isLoadingByProvider: {},
			isMessageListenerAdded: false,
			loginPopup: null, // Reference to login popup
		};
	},
	computed: {
		...mapState('profile', ['profile', 'isErrorTwoFactor']),

		...mapGetters({
			getDefaultRouteName: 'profile/getDefaultRouteName',
		}),

		isProduction() {
			const env = process.env.VUE_APP_NODE_ENV;
			return env === 'production' || env === 'preproduction';
		},

		showLoginForm() {
			// TODO: show login form on production for temporary
			// return !this.isProduction && 'showLoginForm' in this.$route.query;
			return 'showLoginForm' in this.$route.query;
		},
	},
	beforeDestroy() {
		this.unbindReceivingMessage();
	},
	methods: {
		...mapActions({
			postSocialLogin: 'auth/postSocialLogin',
			clearLoginSocialError: 'auth/clearLoginSocialError',
			showToast: 'toast/showToast',
			getProfile: 'profile/getProfile',
		}),

		isProviderLoading(provider) {
			return !!this.isLoadingByProvider[provider];
		},

		// Bind outside methods
		bindReceivingMessage() {
			if (!this.isMessageListenerAdded) {
				this.onReceivingMessageBound = this.onReceivingMessage.bind(this);
				window.addEventListener('message', this.onReceivingMessageBound);
				this.isMessageListenerAdded = true;
			}
		},

		unbindReceivingMessage() {
			if (this.onReceivingMessageBound) {
				window.removeEventListener('message', this.onReceivingMessageBound);
				this.isMessageListenerAdded = false;
			}
		},

		async onReceivingMessage(event) {
			let token;
			try {
				token = JSON.parse(event.data);
			} catch {
				// event.data cannot be parsed as JSON, this message is not token
				return;
			}

			try {
				// 'message' event can be many things
				// we check if the JSON parsed obj having the key 'access_token'
				// to determine this is the message from CRM
				// eslint-disable-next-line camelcase
				if (token?.access_token) {
					await this.postSocialLogin({ token: token.access_token });
					await this.getProfile();

					// If current account is not 2FA go to default route
					if (!this.isErrorTwoFactor) {
						const routeName = this.getDefaultRouteName;
						this.$nextTick(() => {
							this.$router.push({ name: routeName });
						});
					}
				}
			} catch (error) {
				// FYI catch not trigger if get profile and current account is not 2FA
				if (error.response) {
					const { status } = error.response;
					const {
						registration_status: regsiterStatus,
						error: errorType,
						message,
						verified,
					} = error.response.data;
					switch (status) {
						// This user either:
						// 1) Not having account on the site yet
						// 2) Already having account on the site but not approved yet
						// 3) Already having account on the site and approved but account is inactive
						// 4) Already having account on the site and approved but account have to otp
						case 403:
							if (regsiterStatus === REGISTER_STATUS.UNVERIFIED || regsiterStatus === REGISTER_STATUS.REJECTED) {
								// 1)
								this.$router.push({
									name: ROUTE_NAME.REGISTER_SOCIAL,
									query: {
										provider: error.response.data.provider,
									},
									params: {
										crm_token: token.access_token,
										...error.response.data,
									},
								});
							} else if (regsiterStatus === REGISTER_STATUS.PENDING) {
								// 2)
								this.clearLoginSocialError();

								this.showToast({
									content: 'Waiting admin to approve your account',
									header: 'Pending',
									type: 'danger',
								});
							} else if (errorType === SOCIAL_LOGIN_ERROR_TYPE.ACCOUNT_INACTIVE) {
								// 3)
								this.showToast({
									content: message,
									header: 'Error',
									type: 'danger',
								});
							} else if (verified === VERIFIED_STATUS.TWO_FACTOR_VERIFIED) {
								// 4)
								this.$router.push({
									name: ROUTE_NAME.TWO_FACTOR_AUTHENTICATION,
									query: {
										provider: error.response.data.provider,
									},
									params: {
										crm_token: token.access_token,
										...error.response.data,
									},
								});
							}
							break;
						case 401:
							this.$emit('error', error);
							break;
						default:
							break;
					}
				}
			} finally {
				Object.keys(this.isLoadingByProvider).forEach((key) => {
					this.$set(this.isLoadingByProvider, key, false);
				});
			}
		},
		onSocialLogin(provider) {
			this.$set(this.isLoadingByProvider, provider, true);

			let spec = '';
			switch (provider) {
				case PROVIDER_KEY[PROVIDER.IT_EASY]:
					spec = this.windowSpec(500, 500);
					break;
				default:
					break;
			}

			this.bindReceivingMessage();
			const popup = window.open(SOCIAL_PROVIDER_GROUP[provider].window.url, null, spec);
			// Setup message eventlistener to listen to token from CRM
			// We don't have to worry about duplicated eventlistener when user clicked this multiple times
			// Because we use reference to function, addEventListener automatically detect duplication here
			this.loginPopup = popup;

			// Set timeout fallback: if user don't login, popup will close
			setTimeout(() => {
				this.$set(this.isLoadingByProvider, provider, false);
			}, 5000);
		},

		windowSpec(width = 500, height = 500) {
			const top = (window.innerHeight - height) / 2;
			const left = (window.innerWidth - width) / 2;

			return `width=${width},height=${height},top=${top},left=${left}`;
		},
	},
};
</script>

<style lang="scss" scoped>
.divider {
	max-width: 360px;
	margin-top: 12px;
	margin-left: auto;
	margin-right: auto;
	font-size: 14px;
	color: #aaa;
	display: flex;
	align-items: center;

	&::before,
	&::after {
		flex: 1;
		content: "";
		padding: 0.5px;
		background-color: #ddd;
		margin: 10px 0 10px 10px;
	}

	&::before {
		margin: 10px 10px 10px 0;
	}
}

.social-group-list {
	max-width: 360px;
	margin-top: 12px;
	margin-left: auto;
	margin-right: auto;
	display: flex;
	justify-content: center;
}

.btn-social {
	display: flex;
	align-items: center;
	gap: 16px;

	width: 100%;
	border: 1px solid #ddd;
	transition: all 0.3s;
	border-radius: 4px;

	&:hover {
		border-color: #d0d0d0;
	}

	.logo img {
		width: 20px;
		height: 20px;
	}

	.label {
		font-size: rem(14);
		font-weight: 400;
	}
}
</style>
